import { PanelProps } from '@blueprintjs/core';
import { TrackingEventList } from 'portal-sdk';
import * as React from 'react';
import { sendHubspotEvent } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { MAX_CSV_UPLOAD_BYTES } from 'src/constants/limits';
import {
  ProjectFileUploadPanel,
  ProjectFileUploadPanelRefAttributes,
} from 'src/containers/ProjectFileUploadPanel/ProjectFileUploadPanel';
import { useAutoOpenMappingSuggestions } from 'src/containers/ProjectResourceDialog/hooks/useAutoOpenMappingSuggestions';
import { ProjectResourceDialogPanelProps } from 'src/containers/ProjectResourceDialog/types';
import {
  ProjectFileUploadField,
  ProjectFileUploadOptions,
  defaultProjectFileUploadOptions,
} from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import { unparseCsv } from 'src/utils/csv/unparseCsv';
import { generateDateRandomId } from 'src/utils/generateId';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { readFile } from 'src/utils/readFile';

export type ProjectFileUploadCreationPanelProps =
  ProjectResourceDialogPanelProps & {
    uploadedFile: File | null;
  };

export const ProjectFileUploadCreationPanel: React.VFC<
  PanelProps<ProjectFileUploadCreationPanelProps>
> = ({ closePanel, setFooterProps, uploadedFile }) => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const isMounted = useIsMounted();

  const abortParserRef =
    React.useRef<ProjectFileUploadPanelRefAttributes>(null);
  const [isFilePartial, setIsFilePartial] = React.useState(false);
  const [errorText, setErrorText] = React.useState('');
  const [fields, setFields] = React.useState<ProjectFileUploadField[]>([]);
  const [file, setFile] = React.useState<File | null>(null);
  const [fileContents, setFileContents] = React.useState<string>('');
  const [fileName, setFileName] = React.useState('');
  const [fileOptions, setFileOptions] =
    React.useState<ProjectFileUploadOptions>(defaultProjectFileUploadOptions);
  const [filePath, setFilePath] = React.useState('');
  const [rows, setRows] = React.useState<string[][]>([]);

  const resetFile = React.useCallback(() => {
    setIsFilePartial(false);
    setErrorText('');
    setFields([]);
    setFile(null);
    setFileContents('');
    setFileName('');
    setFileOptions(defaultProjectFileUploadOptions);
    setFilePath('');
    setRows([]);
  }, []);

  const isOpen = useReduxSelector(
    (state) => state.ui.isProjectResourceDialogOpen
  );
  const didIsOpenChange = useDidValueChange(isOpen);

  React.useEffect(() => {
    if (didIsOpenChange && !isOpen) {
      abortParserRef.current?.abortParser();
      resetFile();
    }
  }, [didIsOpenChange, isOpen, abortParserRef, resetFile]);

  const { autoOpenMappingSuggestions, isLoading } =
    useAutoOpenMappingSuggestions();

  const createFileUpload = React.useCallback(() => {
    // TODO: determine how to de-dupe files with the same 'path'
    // Due to browser security we can't get the full file path, only the name
    // and extension, so treat uploads as unique and give them a uuid for now.
    // In the future we'll need some handler for recognising when files are
    // uploaded with the same path, and a choice to clone or replace the data.
    const id = generateDateRandomId();

    dispatch(
      projectsActionCreators.addedFileUploadToProject(projectId, {
        name: fileName,
        path: filePath,
        contents: isFilePartial
          ? unparseCsv({
              fields: fields.map(({ value }) => value),
              rows,
              fileOptions,
            })
          : fileContents,
        options: fileOptions,
        fields,
        id,
        partial: isFilePartial,
      })
    );

    return id;
  }, [
    dispatch,
    projectId,
    fields,
    fileContents,
    fileName,
    fileOptions,
    filePath,
    isFilePartial,
    rows,
  ]);

  const handleCreateButtonClick = React.useCallback(() => {
    const fileUploadId = createFileUpload();
    if (fileUploadId) {
      sendHubspotEvent(TrackingEventList.DESIGNER_DEFINE_RESOURCE_CSV);
      dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: false }));
    }
  }, [createFileUpload, dispatch]);

  const handleCreateAndMapButtonClick = React.useCallback(async () => {
    const fileUploadId = createFileUpload();
    if (fileUploadId) {
      sendHubspotEvent(TrackingEventList.DESIGNER_DEFINE_RESOURCE_CSV);
      await autoOpenMappingSuggestions([fileUploadId]);
    }
  }, [autoOpenMappingSuggestions, createFileUpload]);

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({
        createAndMapButtonPending: isLoading,
        createButtonsError: errorText,
        handleBackButtonClick: closePanel,
        handleCreateAndMapButtonClick,
        handleCreateButtonClick,
      });
    }
  }, [
    isMounted,
    setFooterProps,
    errorText,
    closePanel,
    handleCreateAndMapButtonClick,
    handleCreateButtonClick,
    isLoading,
  ]);

  React.useEffect(() => {
    let mounted = true;

    if (uploadedFile) {
      setFilePath(uploadedFile.name);
      setFileName(uploadedFile.name.replace(/\.[^/.]+$/, ''));
      if (uploadedFile.size > MAX_CSV_UPLOAD_BYTES) {
        setIsFilePartial(true);
        setFileContents('');
        setFile(uploadedFile);
        dispatch(projectsActionCreators.uploadedPartialCsv());
      } else {
        readFile(uploadedFile).then((contents) => {
          if (mounted) {
            setFileContents(contents);
            setFile(uploadedFile);
          }
        });
      }
    } else {
      resetFile();
    }

    return () => {
      mounted = false;
    };
  }, [dispatch, uploadedFile, resetFile]);

  return (
    <ProjectFileUploadPanel
      ref={abortParserRef}
      file={file}
      fileName={fileName}
      fileOptions={fileOptions}
      filePath={filePath}
      isFilePartial={isFilePartial}
      setErrorText={setErrorText}
      setFields={setFields}
      setFileName={setFileName}
      setFileOptions={setFileOptions}
      setRows={setRows}
    />
  );
};
